import { useClientApi, useContentResource } from "@ctv/core";
import {
  Button,
  Card,
  Text,
  Token,
  useLocalizedRouterServer,
  View,
} from "@ctv/shared-core/src";
import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";
import IcSystemArrowLeft from "@traveloka/icon-kit-web/react/IcSystemArrowLeft";
import { Fragment, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";
import { useRegisterContent } from "../../_contexts/RegisterContentContext";
import {
  SAVE_AND_EXIT_API,
  SaveAndExitRequest,
  SaveAndExitResponse,
} from "../../apis";
import { RegistrationFormValue } from "../../types";
import { handleProcessDataBeforeSubmit } from "../RegistrationPaymentMethod/helper";
import ReviewPage1 from "./_components/ReviewPage1";
import ReviewPage2 from "./_components/ReviewPage2";
import SubmitFormModal from "./_components/SubmitFormModal";

type Props = {
  onClickBack: () => void;
  onSubmit: (value: RegistrationFormValue) => Promise<void>;
};

export default function RegistrationReview({ onClickBack, onSubmit }: Props) {
  const router = useLocalizedRouterServer();
  const openSnackbar = useDispatchSnackbar();

  const [page, setPage] = useState<number>(1);
  const [isShowSubmitModal, setIsShowSubmitModal] = useState<boolean>(false);

  const { CorporateRegistration: cr } = useContentResource();
  const { token } = useRegisterContent();
  const {
    getValues,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useFormContext<RegistrationFormValue>();

  const save = useClientApi<SaveAndExitResponse, SaveAndExitRequest>({
    domain: "management",
    method: "post",
    path: SAVE_AND_EXIT_API,
  });

  function handleOnClickSaveAndExit() {
    const transformData = handleProcessDataBeforeSubmit(getValues());
    const payload: SaveAndExitRequest = {
      ...transformData,
      token,
    };
    save(payload)
      .then((res) => {
        if (res.success) {
          router.push("/");
          window.scrollTo({ top: 0 });
        } else {
          throw new Error("Failed to save and exit");
        }
      })
      .catch(() => {
        openSnackbar({ message: cr.submitErrorMessage, variant: "alert" });
      });
  }

  const handleContinue = useCallback(() => {
    if (page === 2) {
      setIsShowSubmitModal(true);
    } else {
      setPage((prev) => prev + 1);
    }
  }, [page]);

  const handleBack = useCallback(() => {
    if (page === 1) {
      onClickBack();
    } else {
      setPage((prev) => prev - 1);
    }
  }, [page]);

  return (
    <Fragment>
      <View style={styles.container} row align="start">
        <View style={styles.startContent}>
          {/* Content Header */}
          <Button
            text={cr.back}
            variant="text-primary"
            style={styles.alignSelfStart}
            startIcon={<IcSystemArrowLeft height={16} width={16} />}
            onPress={handleBack}
          />
          <Text variant="headline" style={styles.headline}>
            {cr.reviewFormV2}
          </Text>
          {/* Content Review */}
          {page === 1 && <ReviewPage1 />}
          {page === 2 && <ReviewPage2 />}
          {/* Action Button */}
          <View style={styles.action} row justify="end">
            {page === 2 && (
              <Button
                variant="text-primary"
                text={cr.saveAndExitButtonText}
                onPress={handleOnClickSaveAndExit}
              />
            )}
            <Button
              onPress={handleContinue}
              variant="main-cta"
              style={styles.marginLeftL}
              text={page === 2 ? cr.submit : cr.continueButtonText}
            />
          </View>
        </View>
        <Card style={styles.endContent}>
          <Text style={styles.endContentTitle} variant="ui-small">
            {cr.moreTimeInfo}
          </Text>
          <Text variant="ui-tiny" ink="black-secondary">
            {cr.moreTimeInfoBody}
          </Text>
        </Card>
      </View>
      <SubmitFormModal
        isVisible={isShowSubmitModal}
        onClose={() => setIsShowSubmitModal(false)}
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={isSubmitting}
      />
    </Fragment>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 960,
    position: "relative",
    marginTop: Token.spacing.xxl,
    marginHorizontal: "auto",
  },
  // Start Content
  startContent: {
    flex: 1,
    marginRight: Token.spacing.xxxl,
  },
  headline: {
    marginTop: Token.spacing.m,
    marginBottom: Token.spacing.xxs,
  },
  action: {
    marginVertical: Token.spacing.xl,
  },
  // End Content
  endContent: {
    width: 266,
    padding: Token.spacing.m,
    // @ts-expect-error
    position: "sticky",
    top: 2 * Token.spacing.xl + Token.spacing.xxxl,
  },
  endContentTitle: {
    fontWeight: "600",
    marginBottom: Token.spacing.m,
  },
  // General styles
  alignSelfStart: {
    alignSelf: "flex-start",
  },
  marginLeftL: {
    marginLeft: Token.spacing.l,
  },
});
